"use client";

import React from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { cubicBezier, useTransform, type MotionValue } from "framer-motion";

import Chip from "@/components/bastian-keller/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { MotionDiv, MotionP } from "@/components/global/utils/motionTags";
import { promoteLayer } from "@/components/global/utils/motionLayer";
import { useMotionMode } from "@/components/global/utils/motionMode";
import { EASE_OUT } from "@/components/global/utils/motionTiming";
import {
  CROSSING_OFFSET,
  REVEAL_OFFSET,
  REVEAL_WINDOW,
  revealStart,
  useReveal,
  useRevealProgress,
} from "@/components/global/utils/scrollReveal";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

/**
 * The collage is art-directed: ten fixed slots scattered across the Figma frame.
 * Sanity supplies the photo and caption for each slot, the geometry lives here.
 * Classes are written out in full because Tailwind can't see interpolated strings.
 *
 * Geometry is 1:1 with the Figma frame (1920x2468 @ 1px = 0.125rem). `placement`
 * is the outer card box (photo + its 8px frame padding); `aspect` is the ratio of
 * the photo rect *inside* that padding, which is what sizes the card below lg.
 * `depth` is the parallax multiplier — see PARALLAX_DRIFT below.
 *
 * `mobile` is the same slot on the 430 frame, where the scatter becomes a single
 * indented column. That frame runs the photos in a different sequence to the one
 * Sanity holds them in, so each slot also carries the `order` that puts its card
 * where the mobile design wants it. The pairing was read off the frame by shape
 * and caption together; only the sixth position disagrees on caption, which is
 * forced — the mobile frame was drawn with one more "crypto day event" photo and
 * one fewer "basti in the office" than the page actually has.
 */
const SLOTS = [
  { placement: "lg:left-[88.875rem] lg:top-[47rem] lg:w-[59.9167rem] lg:h-[52.25rem]", mobile: "order-[1] ml-[5.75rem] w-[40.625rem]", aspect: "aspect-[695/561]", depth: 0.85 },
  { placement: "lg:left-[12.875rem] lg:top-[56.125rem] lg:w-[58.5rem] lg:h-[42.5rem]", mobile: "order-[0] ml-0 w-[43.75rem]", aspect: "aspect-[113/74]", depth: 0.3 },
  { placement: "lg:left-[176rem] lg:top-[66.75rem] lg:w-[39.25rem] lg:h-[52.25rem]", mobile: "order-[5] ml-[8.5rem] w-[32.86rem]", aspect: "aspect-[149/187]", depth: 1 },
  { placement: "lg:left-[26.875rem] lg:top-[110rem] lg:w-[47.5rem] lg:h-[52.25rem]", mobile: "order-[2] ml-[10.43rem] w-[39.32rem]", aspect: "aspect-[182/187]", depth: 0.45 },
  { placement: "lg:left-[103rem] lg:top-[117.625rem] lg:w-[55.5rem] lg:h-[61.75rem]", mobile: "order-[8] ml-[8.625rem] w-[41.125rem]", aspect: "aspect-[214/225]", depth: 0.2 },
  { placement: "lg:left-[166.5rem] lg:top-[169.25rem] lg:w-[59.9167rem] lg:h-[52.25rem]", mobile: "order-[4] ml-0 w-[46.25rem]", aspect: "aspect-[695/561]", depth: 0.7 },
  { placement: "lg:left-[38.625rem] lg:top-[178rem] lg:w-[42rem] lg:h-[52.25rem]", mobile: "order-[3] ml-0 w-[31.75rem]", aspect: "aspect-[160/187]", depth: 0.55 },
  { placement: "lg:left-[106.75rem] lg:top-[198.125rem] lg:w-[39.25rem] lg:h-[52.25rem]", mobile: "order-[7] ml-0 w-[28.73rem]", aspect: "aspect-[149/187]", depth: 0.85 },
  { placement: "lg:left-[27.625rem] lg:top-[245.875rem] lg:w-[59.9167rem] lg:h-[52.25rem]", mobile: "order-[9] ml-0 w-[48.25rem]", aspect: "aspect-[695/561]", depth: 0.35 },
  { placement: "lg:left-[155.75rem] lg:top-[255.625rem] lg:w-[58.5rem] lg:h-[42.5rem]", mobile: "order-[6] ml-0 w-[49.625rem]", aspect: "aspect-[113/74]", depth: 0.6 },
];

/**
 * Half-travel of the deepest slot, in rem so it tracks the fluid root size.
 * Depths are tuned against the vertical gaps in the Figma scatter: the widest
 * depth spread sits between slots that are far apart, so nothing collides at
 * either end of the scroll.
 */
/** Photos past the tenth have no slot to go in — see ImagePrefetch, which skips them. */
export const GALLERY_SLOT_COUNT = SLOTS.length;

const PARALLAX_DRIFT = 7;

/**
 * The caption sits at 60% in Figma, so that — not 1 — is where it has to land:
 * a scrubbed opacity writes an inline value that would win over a Tailwind
 * `opacity-60` class.
 */
const CAPTION_OPACITY = 0.6;

/**
 * How far inside the photo the frame starts before it pulls open. Bigger than the
 * 1rem resting padding on purpose: the frame is drawn *over* the photo at first,
 * so the travel outward is what you actually read as the focus pull.
 */
const FRAME_START_INSET = 5;

/**
 * The frame overshoots its resting inset before settling. A spring would have done
 * this for free, but springs run on a clock — scrubbed off scroll, the overshoot
 * has to be an explicit waypoint in the curve instead.
 *
 * The overshoot is kept under half the resting padding on purpose: enough that the
 * frame reads as settling rather than stopping dead, small enough that it never
 * looks like it bounced off something.
 */
const FRAME_STOPS = [0, 0.72, 1];
const FRAME_INSETS = [`${FRAME_START_INSET}rem`, "-0.15rem", "0rem"];

/** Caption, frame and photo — three slices of the card's own scroll range. */
const CARD_PARTS = 3;

/**
 * The photo gets a gentler curve than the shared expo ease the rest of the card
 * runs on. Scrubbed off scroll, expo covers almost all of its distance in the
 * first third of the crossing and crawls through the rest — on a zoom that reads
 * as a snap followed by a drift, not as a settle. A plain cubic ease-out spends
 * its distance evenly enough that the scale stays legible the whole way down.
 */
const PHOTO_EASE = cubicBezier(0.33, 1, 0.68, 1);

/**
 * Small enough that the photo never looks like it is being pushed at you — the
 * zoom is there to give the fade somewhere to land, not to be noticed on its own.
 */
const PHOTO_START_SCALE = 1.06;

/** The fade is done well before the zoom is, so the photo settles already solid. */
const PHOTO_FADE_END = 0.6;

/**
 * How far the photo is pushed by the cursor crossing its edge, in rem. Kept under
 * half the overhang HOVER_SCALE buys (see below) so the trailing edge of the photo
 * never pulls back inside the frame and leaves a gap.
 */
const HOVER_SHIFT = 1.5;

/**
 * The photo grows into the push rather than just sliding: the shift alone would
 * uncover the corner it moves away from, since the photo is exactly the size of
 * its clip. The extra size is spare material on all four sides, so whichever way
 * the push lands there is something to slide into view.
 */
const HOVER_SCALE = 1.08;

/**
 * Loose enough to overshoot slightly — the push reads as the photo being knocked
 * aside, and a knock that stops dead reads as a slide instead. Damped hard enough
 * that re-entering from a new edge mid-flight redirects rather than wobbles.
 */
const HOVER_SPRING = {
  type: "spring",
  stiffness: 260,
  damping: 26,
  mass: 0.5,
} as const;

/**
 * While the cursor is on one photo, every other card drops to this. The whole
 * card dims rather than just its photo, so the caption goes with it — nested
 * opacity multiplies, which lands the caption at 0.2 of the 0.6 it rests at.
 */
const DIM_OPACITY = 0.2;

/**
 * Slower than the push, which is a knock and wants to be instant. The dim is the
 * background receding, and a fast fade there reads as a flicker when the cursor
 * crosses the gap between two neighbouring slots.
 */
const DIM_TRANSITION = { duration: 0.35, ease: EASE_OUT } as const;

/**
 * Which edge the pointer crossed, expressed as the inward normal of that edge:
 * coming in over the right edge gives (-1, 0), over the top edge gives (0, 1). So
 * the photo is always pushed the way the cursor was travelling.
 *
 * The offset from the centre is normalised against each half-axis before the two
 * are compared, which puts the split on the rect's own diagonals. Comparing raw
 * pixels instead would split at 45° and hand nearly every entry on the wide slots
 * to the left and right edges, including ones that came straight down the top.
 */
function entryPush(rect: DOMRect, clientX: number, clientY: number) {
  const x = (clientX - (rect.left + rect.width / 2)) / (rect.width / 2);
  const y = (clientY - (rect.top + rect.height / 2)) / (rect.height / 2);

  if (Math.abs(x) > Math.abs(y)) return { x: x > 0 ? -1 : 1, y: 0 };
  return { x: 0, y: y > 0 ? -1 : 1 };
}

function GalleryCard({
  item,
  slot,
  drift,
  parallax,
  reduceMotion,
  dimmed,
  onHoverChange,
}: {
  item: PageBastianKellerSchema["galleryItems"][number];
  slot: (typeof SLOTS)[number];
  drift: MotionValue<number>;
  parallax: boolean;
  reduceMotion: boolean;
  dimmed: boolean;
  onHoverChange: (hovered: boolean) => void;
}) {
  const cardRef = React.useRef<HTMLDivElement>(null);

  // the drift spans the whole section, the reveal only this card's own crossing,
  // so they come off two different ranges
  const travel = parallax ? slot.depth * PARALLAX_DRIFT : 0;
  const y = useTransform(drift, [0, 1], [`${travel}rem`, `${-travel}rem`]);

  const progress = useRevealProgress(cardRef, REVEAL_OFFSET, reduceMotion);
  const caption = useReveal(progress, 0, CARD_PARTS, reduceMotion);
  const frame = useReveal(progress, 1, CARD_PARTS, reduceMotion);

  const captionOpacity = useTransform(caption.reveal, [0, 1], [0, CAPTION_OPACITY]);

  const frameInset = useTransform(frame.reveal, FRAME_STOPS, FRAME_INSETS);
  const frameRadius = useTransform(frame.reveal, [0, 1], ["0.375rem", "1.5rem"]);
  const frameColor = useTransform(
    frame.reveal,
    [0, 0.4],
    ["rgba(17,38,40,0)", "rgba(17,38,40,0.5)"],
  );

  // the photo takes the same slice of the range as the other parts but not their
  // easing, so it is built off the raw progress rather than through useReveal —
  // re-easing an already-eased value would just compound the two curves
  const photoStart = revealStart(2, CARD_PARTS);
  const photoReveal = useTransform(
    progress,
    [photoStart, photoStart + REVEAL_WINDOW],
    reduceMotion ? [1, 1] : [0, 1],
    { ease: PHOTO_EASE },
  );
  const photoOpacity = useTransform(photoReveal, [0, PHOTO_FADE_END], [0, 1]);
  const photoScale = useTransform(photoReveal, [0, 1], [PHOTO_START_SCALE, 1]);

  // null is the resting state, so leaving always returns the photo the way it
  // came — the push is only direction-aware on the way in, where the cursor's
  // travel is something you can still feel
  const [push, setPush] = React.useState<{ x: number; y: number } | null>(null);

  const handlePointerEnter = (event: React.PointerEvent<HTMLDivElement>) => {
    // touch reports an enter at the moment of the tap, with no travel behind it
    if (event.pointerType !== "mouse") return;

    // the dim is opacity alone, so it still runs with motion off — only the push,
    // which is travel, is gated on that
    onHoverChange(true);
    if (reduceMotion) return;

    setPush(
      entryPush(
        event.currentTarget.getBoundingClientRect(),
        event.clientX,
        event.clientY,
      ),
    );
  };

  const handlePointerLeave = () => {
    onHoverChange(false);
    setPush(null);
  };

  return (
    <MotionDiv
      ref={cardRef}
      style={{ y }}
      /*
        Promoted: the scatter drifts for the whole 308rem of the section, so every
        one of these is moving for almost the entire time it is on screen — the
        longest continuous transform on the page, over ten full-quality photos.
        The gate matters as much as the promotion: below lg the cards flow in a
        masonry column with no drift at all, and ten layers there would be pure
        cost on the devices least able to carry it.
      */
      transformTemplate={parallax ? promoteLayer : undefined}
      /*
        The dim rides the outer card rather than the photo: the photo's opacity is
        already a scrubbed MotionValue driven by the reveal, and a second writer on
        that property would fight it. Up here the property is otherwise unused.
      */
      initial={false}
      animate={{ opacity: dimmed ? DIM_OPACITY : 1 }}
      transition={reduceMotion ? { duration: 0 } : DIM_TRANSITION}
      className={cn(
        "lg:absolute lg:ml-0 lg:w-auto",
        slot.mobile,
        slot.placement,
      )}>
      <div className="flex h-full flex-col items-start gap-[0.56rem] lg:gap-[0.75rem]">
        {item?.caption && (
          <MotionP
            style={{ opacity: captionOpacity, y: caption.y }}
            className="m-0 rounded-[0.5rem] bg-[#112628]/[.05] px-[0.75rem] py-[0.5rem] font-geistmono text-[1.75rem] leading-none text-black">
            {item.caption}
          </MotionP>
        )}

        <div className="relative w-full p-[1rem] lg:flex-1">
          {/*
            The frame is an overlay rather than a border on the padded box so it
            can grow past the photo without nudging it — at rest its inset is 0,
            which lands it exactly where the Figma stroke sits. It needs the
            z-index because it starts *inside* the photo, and the photo is a
            positioned sibling that would otherwise paint over it.
          */}
          <MotionDiv
            aria-hidden
            style={{
              top: frameInset,
              right: frameInset,
              bottom: frameInset,
              left: frameInset,
              borderRadius: frameRadius,
              borderColor: frameColor,
            }}
            className="pointer-events-none absolute z-10 border-[0.125rem] border-solid"
          />

          <div
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
            className={cn(
              "relative w-full overflow-hidden rounded-[0.75rem] lg:aspect-auto lg:h-full",
              slot.aspect,
            )}>
            {item?.image?.src && (
              <MotionDiv
                style={{ opacity: photoOpacity, scale: photoScale }}
                className="absolute inset-0">
                {/*
                  The push rides its own element inside the scrubbed one: both are
                  scales, and a single node can only hold one. Nested, they
                  multiply, so the hover can't fight the reveal for the transform.
                */}
                <MotionDiv
                  animate={{
                    x: `${(push?.x ?? 0) * HOVER_SHIFT}rem`,
                    y: `${(push?.y ?? 0) * HOVER_SHIFT}rem`,
                    scale: push ? HOVER_SCALE : 1,
                  }}
                  transition={HOVER_SPRING}
                  className="absolute inset-0">
                  <Image
                    className="absolute left-0 top-0 !h-full !w-full object-cover"
                    src={item.image}
                    alt={item?.caption || ""}
                    quality={100}
                  />
                </MotionDiv>
              </MotionDiv>
            )}
          </div>
        </div>
      </div>
    </MotionDiv>
  );
}

export default function GallerySection({
  data,
  className,
}: {
  data: PageBastianKellerSchema;
  className?: string;
}) {
  const items = data?.galleryItems?.slice(0, SLOTS.length);

  const sectionRef = React.useRef<HTMLElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);

  // which card the cursor is on, so the other nine can step back. It lives here
  // rather than in the cards because it is the one piece of state each of them
  // needs to know about all the others.
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);

  // the scatter only exists at lg — below it the cards flow in a masonry column,
  // where drifting them vertically would just tear the rhythm apart
  const { reduceMotion, parallax } = useMotionMode();

  // a touch of lag behind the raw scroll position keeps the drift from feeling
  // welded to the wheel; damped hard enough not to overshoot into a neighbour
  const smoothProgress = useRevealProgress(sectionRef, CROSSING_OFFSET, !parallax);

  // the header crosses the screen well before the first cards, so it is scrubbed
  // off its own range rather than the section's
  const headerProgress = useRevealProgress(headerRef, REVEAL_OFFSET, reduceMotion);
  const chip = useReveal(headerProgress, 0, 2, reduceMotion);
  const title = useReveal(headerProgress, 1, 2, reduceMotion);

  return (
    <section
      ref={sectionRef}
      className={cn("relative bg-[#F3F2F1] overflow-clip", className)}>
      <div className="relative mx-auto w-[min(240rem,100%)] lg:h-[308.5rem]">
        <DecorationLine
          className="left-[1rem] lg:left-[7.125rem]"
          orientation="vertical"
          style="dark"
        />
        <DecorationLine
          className="right-[1rem] lg:right-[7rem]"
          orientation="vertical"
          style="dark"
        />

        {/* the rule the section opens under: 32px in on the mobile frame, 67px on the desktop one */}
        <DecorationLine
          className="top-[4rem] lg:top-[8.375rem]"
          orientation="horizontal"
          style="dark"
        />

        {/* header */}
        <div
          ref={headerRef}
          className="flex flex-col items-start gap-[2rem] pt-[12rem] pr-[2rem] pl-[3.5rem] text-left lg:absolute lg:left-1/2 lg:top-[17.75rem] lg:w-[92.375rem] lg:-translate-x-1/2 lg:items-center lg:gap-[3rem] lg:px-0 lg:pt-0 lg:text-center">
          {data?.galleryChip?.text && (
            <MotionDiv style={{ opacity: chip.reveal, y: chip.y }}>
              <Chip
                image={data?.galleryChip?.icon}
                text={data?.galleryChip?.text}
              />
            </MotionDiv>
          )}

          {data?.galleryTitle && (
            <MotionDiv
              style={{ opacity: title.reveal, y: title.y }}
              /* the alignment rules live in the components layer, so these utilities win */
              className="pixel-highlight richtext richtext-center items-start text-left lg:w-[88.125rem] lg:items-center lg:text-center prose-p:font-geist prose-p:text-[3rem] prose-p:leading-[1.2] prose-p:tracking-[-0.02em] prose-p:text-black/[.82] lg:prose-p:text-[6rem] prose-em:font-ivy prose-em:text-[3.25rem] lg:prose-em:text-[6.5rem] prose-em:tracking-normal prose-em:text-black">
              <PortableText
                value={data.galleryTitle}
                components={richTextComponent}
              />
            </MotionDiv>
          )}
        </div>

        {/*
          Below lg the scatter collapses into a two-column masonry so the photos
          keep their varied shapes without the absolute geometry.
        */}
        {items && items.length > 0 && (
          <div className="flex flex-col gap-[3rem] px-[2rem] pt-[5rem] pb-[8rem] lg:block lg:gap-0 lg:p-0">
            {items.map((item, index) => (
              <GalleryCard
                key={index}
                item={item}
                slot={SLOTS[index]}
                drift={smoothProgress}
                parallax={parallax}
                reduceMotion={reduceMotion}
                dimmed={hoveredIndex !== null && hoveredIndex !== index}
                /*
                  Leaving only clears the hover if this card still owns it. Moving
                  between two touching slots fires the new card's enter before the
                  old one's leave, and an unguarded reset there would blank the
                  hover that had just been set and flash all ten cards back to full.
                */
                onHoverChange={(hovered) =>
                  setHoveredIndex((current) =>
                    hovered ? index : current === index ? null : current,
                  )
                }
              />
            ))}
          </div>
        )}
      </div>

      {/* desktop only: below lg the Companies section opens on its own rule at this edge */}
      <DecorationLine
        className="bottom-0 hidden lg:block"
        orientation="horizontal"
        style="dark"
      />
    </section>
  );
}
